// components/shared/DataTable.js
// טבלה כללית — sorting, filtering, pagination, actions, responsive
//
// ★ חובה: HTML <table> אמיתי עם thead/tbody/tr/th/td
//   כדי לתאום ל-testHarness:
//   - getTableData(testId) → querySelectorAll('thead th'), querySelectorAll('tbody tr td')
//   - clickTableRow(testId, rowIndex) → querySelectorAll('tbody tr')[rowIndex].click()
//   - clickTableAction(testId, rowIndex, actionTestId) → tbody tr[i].querySelector([data-testid])
//   - getTableRowCount(testId) → querySelectorAll('tbody tr').length
//
// שימוש:
//   <DataTable
//     testId="users__table"
//     columns={[
//       { key: 'full_name', label: t('users.fullName'), sortable: true },
//       { key: 'is_admin', label: t('users.role'), render: (val, row) => <Chip ... /> },
//     ]}
//     rows={users}
//     actions={[
//       { key: 'edit', icon: <EditIcon />, label: t('common.edit'), onClick: (row) => ... },
//       { key: 'delete', icon: <DeleteIcon />, label: t('common.delete'), onClick: (row) => ..., color: 'error' },
//     ]}
//     loading={loading}
//     searchable
//     emptyStateProps={{ type: 'noData', actionLabel: t('users.addUser'), onAction: handleAdd }}
//   />

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';
import { visuallyHidden } from '@mui/utils';

import Loading from './Loading';
import EmptyState from './EmptyState';

// =============================================================================
// Sorting helpers
// =============================================================================
function descendingComparator(a, b, orderBy) {
  const aVal = a[orderBy];
  const bVal = b[orderBy];

  // Handle nulls — always sort to end
  if (aVal == null && bVal == null) return 0;
  if (aVal == null) return 1;
  if (bVal == null) return -1;

  // Numeric comparison
  if (typeof aVal === 'number' && typeof bVal === 'number') {
    return bVal - aVal;
  }

  // Boolean comparison (true before false in descending)
  if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
    return bVal === aVal ? 0 : bVal ? 1 : -1;
  }

  // String comparison — Hebrew-aware
  const strA = String(aVal);
  const strB = String(bVal);
  return strB.localeCompare(strA, 'he');
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilized = array.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1]; // preserve original order for equal elements
  });
  return stabilized.map((el) => el[0]);
}

// =============================================================================
// Search helper
// =============================================================================
function matchesSearch(row, columns, searchTerm) {
  if (!searchTerm) return true;
  const lower = searchTerm.toLowerCase();

  return columns.some((col) => {
    const val = row[col.key];
    if (val == null) return false;
    return String(val).toLowerCase().includes(lower);
  });
}

// =============================================================================
// DataTable Component
// =============================================================================
export default function DataTable({
  testId,
  columns = [],
  rows = [],
  actions = [],
  loading = false,
  searchable = false,
  searchPlaceholder = null,
  emptyStateProps = null,
  defaultSortKey = null,
  defaultSortDir = 'asc',
  rowsPerPageOptions = [5, 10, 25],
  onRowClick = null,
  stickyHeader = false,
  dense = false,
  getRowId = (row) => row.id,
  toolbar = null,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:767px)');

  // Glassmorphism card styles
  const glassCard = theme.custom?.glassmorphism?.card || {};

  // ===================================================================
  // State
  // ===================================================================
  const [order, setOrder] = useState(defaultSortDir);
  const [orderBy, setOrderBy] = useState(defaultSortKey);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0] || 10);

  // ===================================================================
  // Sort handler
  // ===================================================================
  const handleSort = useCallback((columnKey) => {
    const isAsc = orderBy === columnKey && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnKey);
  }, [order, orderBy]);

  // ===================================================================
  // Search handler
  // ===================================================================
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setPage(0); // חזור לעמוד ראשון בחיפוש
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setPage(0);
  }, []);

  // ===================================================================
  // Pagination handlers
  // ===================================================================
  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  // ===================================================================
  // Processed rows — filter → sort → paginate
  // ===================================================================
  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    return rows.filter((row) => matchesSearch(row, columns, searchTerm));
  }, [rows, columns, searchTerm]);

  const sortedRows = useMemo(() => {
    if (!orderBy) return filteredRows;
    return stableSort(filteredRows, getComparator(order, orderBy));
  }, [filteredRows, order, orderBy]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  // Reset page if filtered results shrink below current page
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredRows.length / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredRows.length, rowsPerPage, page]);

  // ===================================================================
  // Has actions column?
  // ===================================================================
  const hasActions = actions.length > 0;

  // ===================================================================
  // Render
  // ===================================================================
  return (
    <Paper
      data-testid={testId}
      elevation={0}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        ...glassCard,
      }}
    >
      {/* Toolbar: search + custom toolbar */}
      {(searchable || toolbar) && (
        <Box
          data-testid={`${testId}__toolbar`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2,
            py: 1.5,
            flexWrap: 'wrap',
          }}
        >
          {/* Custom toolbar (e.g. add button, filters) */}
          {toolbar}

          {/* Search field */}
          {searchable && (
            <TextField
              data-testid={`${testId}__input--search`}
              size="small"
              placeholder={searchPlaceholder || t('common.search')}
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{ minWidth: 200, maxWidth: 320, mr: toolbar ? 'auto' : 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      aria-label={t('common.clear')}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          )}
        </Box>
      )}

      {/* Loading overlay */}
      {loading && (
        <Loading message={t('common.loading')} />
      )}

      {/* Table */}
      {!loading && (
        <>
          <TableContainer
            sx={{
              overflowX: 'auto', // horizontal scroll ב-mobile
              maxHeight: stickyHeader ? 600 : undefined,
            }}
          >
            <Table
              stickyHeader={stickyHeader}
              size={dense ? 'small' : 'medium'}
              aria-label={testId}
            >
              {/* ============ THEAD ============ */}
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align || 'right'}
                      sx={{
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        width: col.width || 'auto',
                        bgcolor: theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.03)'
                          : 'rgba(0,0,0,0.02)',
                      }}
                      sortDirection={orderBy === col.key ? order : false}
                    >
                      {col.sortable ? (
                        <TableSortLabel
                          active={orderBy === col.key}
                          direction={orderBy === col.key ? order : 'asc'}
                          onClick={() => handleSort(col.key)}
                        >
                          {col.label}
                          {orderBy === col.key && (
                            <Box component="span" sx={visuallyHidden}>
                              {order === 'desc' ? t('common.sortDesc') : t('common.sortAsc')}
                            </Box>
                          )}
                        </TableSortLabel>
                      ) : (
                        col.label
                      )}
                    </TableCell>
                  ))}

                  {/* Actions header */}
                  {hasActions && (
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        width: actions.length * 44 + 16,
                        bgcolor: theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.03)'
                          : 'rgba(0,0,0,0.02)',
                      }}
                    >
                      {t('common.actions')}
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              {/* ============ TBODY ============ */}
              <TableBody>
                {paginatedRows.length > 0 ? (
                  paginatedRows.map((row, rowIndex) => {
                    const rowId = getRowId(row);
                    const globalRowIndex = page * rowsPerPage + rowIndex;

                    return (
                      <TableRow
                        key={rowId ?? rowIndex}
                        data-testid={`${testId}__row--${globalRowIndex}`}
                        hover
                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                        sx={{
                          cursor: onRowClick ? 'pointer' : 'default',
                          transition: 'background-color 0.15s ease',
                        }}
                      >
                        {columns.map((col) => (
                          <TableCell
                            key={col.key}
                            align={col.align || 'right'}
                          >
                            {col.render
                              ? col.render(row[col.key], row, globalRowIndex)
                              : row[col.key] ?? '—'
                            }
                          </TableCell>
                        ))}

                        {/* Action buttons */}
                        {hasActions && (
                          <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                            {actions.map((action) => {
                              // Conditional visibility
                              if (action.show && !action.show(row)) return null;

                              return (
                                <Tooltip
                                  key={action.key}
                                  title={action.label}
                                  arrow
                                >
                                  <IconButton
                                    data-testid={`${testId}__action--${action.key}-${globalRowIndex}`}
                                    size="small"
                                    color={action.color || 'default'}
                                    onClick={(e) => {
                                      e.stopPropagation(); // don't trigger row click
                                      action.onClick(row);
                                    }}
                                    aria-label={`${action.label} ${rowId || ''}`}
                                    disabled={action.disabled ? action.disabled(row) : false}
                                  >
                                    {action.icon}
                                  </IconButton>
                                </Tooltip>
                              );
                            })}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                ) : (
                  /* Empty state row — no results after filter */
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + (hasActions ? 1 : 0)}
                      sx={{ border: 'none' }}
                    >
                      {searchTerm ? (
                        // No search results
                        <EmptyState
                          type="noResults"
                          sx={{ py: 4 }}
                        />
                      ) : (
                        // No data at all
                        <EmptyState
                          {...(emptyStateProps || { type: 'noData' })}
                          sx={{ py: 4, ...(emptyStateProps?.sx || {}) }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {filteredRows.length > rowsPerPageOptions[0] && (
            <TablePagination
              data-testid={`${testId}__pagination`}
              component="div"
              count={filteredRows.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={rowsPerPageOptions}
              labelRowsPerPage={t('common.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) =>
                t('common.paginationOf', { from, to, count })
              }
              sx={{
                borderTop: `1px solid ${theme.palette.divider}`,
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  fontSize: '0.8125rem',
                },
              }}
            />
          )}

          {/* Row count summary — shown when pagination hidden */}
          {filteredRows.length > 0 && filteredRows.length <= rowsPerPageOptions[0] && (
            <Box sx={{ textAlign: 'center', py: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="caption" color="text.secondary">
                {t('common.totalRows', { count: filteredRows.length })}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}
